import { Component, EventEmitter, Input, OnInit, Output, Inject, PLATFORM_ID, OnDestroy } from '@angular/core';
import {IconComponent} from '../icon/icon.component';
import {Icons} from '../../../model/icon';
import {NgClass, NgForOf, isPlatformBrowser} from '@angular/common';
import {Circle, Continent} from '../../../model/circle';
import {calculateCoordinate} from '../../../functions/calculate-coordinate';
import {TextsComponent} from '../texts/texts.component';
import {CircleDestination} from '../../../model/circle-destination';
import {MainDestinationsMode} from '../../pages/main/items/main-destinations/main-destinations.component';
import {environment} from '../../../environments/environment';
import {fromEvent, Subscription, debounceTime} from 'rxjs';

@Component({
  selector: 'app-circle',
  imports: [
    IconComponent,
    NgForOf,
    NgClass,
    TextsComponent
  ],
  templateUrl: './circle.component.html',
  standalone: true,
  styleUrl: './circle.component.scss'
})
export class CircleComponent implements OnInit, OnDestroy {
  @Input() disable: boolean = false;
  @Input() destinations: CircleDestination = new CircleDestination(
    {
      titles: [],
      icons: [],
      urls: [],
    }
  );
  @Input() mode: MainDestinationsMode = 'Countries';
  @Output() emitActive = new EventEmitter();
  @Output() emitObj = new EventEmitter();
  @Output() emitContinent = new EventEmitter();

  circles: Circle[] = [];
  circlesVail: Circle[] = [];
  continents: Continent[] = [];
  deg: number = 0;
  active: number = 0;
  clockwise: boolean = true;

  circleItemIndex: number = 0;
  indexCountMax: number = 0;
  bigRadius = [
    {
      x: 0,
      y: 0,
      deg: 0,
    }
  ]

  private isBrowser: boolean;
  private resizeSubscription?: Subscription;
  private lastStableWidth: number = 0;
  private resizeTimeout: any;
  private isIOS = false;

  constructor(@Inject(PLATFORM_ID) private platformId: any) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  getPositionCircleVail(circle: Circle){
    const deg = circle.deg ?? 0;
    return `transform: scale(1) translate(${circle.x}px, ${circle.y}px) rotate(${deg - 40}deg);`
  }

  getPositionCircle(circle: Circle){
    return  `transform: translate(${circle.x}px, ${circle.y}px);`
  }

  getPositionContinent(continent: Continent){
    const x = continent.id ==  this.active ? continent.xb : continent.x;
    const y = continent.id ==  this.active ? continent.yb : continent.y;
    return `transform: translate(${x}px, ${y}px) rotate(${-this.deg}deg);`
  }

  getDeg() {
    return  `transform: rotate(${this.deg}deg);`
  }

  getSvg(continent: Continent) {
    if (this.mode === 'Countries') {
      return environment.s3Prefix + continent.icon?.src;
    } else return continent.icon?.src;
  }

  enterContinent(continent: Continent) {
    continent.isHovered = true;
  }

  leaveContinent(continent: Continent) {
    continent.isHovered = false;
  }

  clickContinent(continent: Continent) {
    if (this.mode === 'Continents') {
      this.emitContinent.emit(continent);
    }
    if (!this.disable) {
      switch (continent.id) {
        case this.active: {
          this.emitObj.emit(continent);
          break;
        }
        case this.active + 1:  {
          this.active++;
          this.changeCircleElements(this.active,  this.destinations.titles.length, true);
          this.deg -= 60;
          this.clockwise = true;
          break;
        }
        case this.active - 1:  {
          this.active--;
          this.changeCircleElements(this.active,  this.destinations.titles.length, false);
          this.deg += 60;
          this.clockwise = false;
          break;
        }
        case 0 :
        case this.continents.length - 1: {
          if (this.active == this.continents.length - 1) {
            this.active = 0;
            this.changeCircleElements(this.active, this.destinations.titles.length, true);
            this.deg -= 60;
            this.clockwise = true;
          } else {
            this.active = this.continents.length - 1;
            this.changeCircleElements(this.active, this.destinations.titles.length, false);
            this.deg += 60;
            this.clockwise = false;
          }
          break;
        }
        default: {
          break;
        }
      }
      this.emitActive.emit(this.circleItemIndex);
    }
  }

  isNoneContinent(id?: number) {
    if (id != undefined) {
      switch (id) {
        case this.active:
        case this.active + 1:
        case this.active - 1:
          return false;
        case 5: {
          return this.active != 0;
        }
        case 0: {
          return  this.active != this.continents.length - 1;
        }
        default: {
          return true;
        }
      }
    } else return true;
  }

  ngOnInit() {
    if (this.isBrowser) {
      this.detectIOS();
      this.lastStableWidth = document.documentElement.clientWidth;
      this.setCircle();
      this.resizeSubscription = fromEvent(window, 'resize')
        .pipe(debounceTime(300))
        .subscribe(() => {
          this.handleResize();
        });
    }
    this.indexCountMax = this.destinations.titles.length;
  }

  ngOnDestroy(): void {
    if (this.resizeSubscription) {
      this.resizeSubscription.unsubscribe();
    }
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
  }

  private detectIOS(): void {
    this.isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
  }

  private handleResize(): void {
    if (!this.isBrowser) return;
    const currentWidth = document.documentElement.clientWidth;
    const widthChange = Math.abs(currentWidth - this.lastStableWidth);
    if (this.isIOS && widthChange < 10) {
      return;
    }
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this.lastStableWidth = currentWidth;
      this.setCircle();
    }, 200);
  }

  setCircle() {
    if (!this.isBrowser) return;
    const stableWidth = document.documentElement.clientWidth;
    this.lastStableWidth = stableWidth;
    if (stableWidth < 769 && stableWidth > 576) {
      this.bigRadius = calculateCoordinate(6, 500, 1, 1);
      this.circlesVail = calculateCoordinate(4, 150, 1, 1, 0);
      this.circles = calculateCoordinate(6, 222.5, 1, 1);
      this.continents = calculateCoordinate(6, 320, 1, 1);
    } else if (stableWidth < 576) {
      // this.bigRadius = calculateCoordinate(6, 450, 1, 1);
      // this.circlesVail = calculateCoordinate(4, 100, 1, 1, 0);
      // this.circles = calculateCoordinate(6, 200, 1, 1);
      // this.continents = calculateCoordinate(6, 260, 1, 1);

      this.bigRadius = calculateCoordinate(6, 450, 1, 1);
      this.circlesVail = calculateCoordinate(4, 100, 1, 1, 0);
      this.circles = calculateCoordinate(6, 210, 1, 1);
      this.continents = calculateCoordinate(6, 260, 1, 1);

      // this.bigRadius = calculateCoordinate(6, 470, 1, 1);
      // this.circlesVail = calculateCoordinate(4, 150, 1, 1, 0);
      // this.circles = calculateCoordinate(6, 222.5, 1, 1);
      // this.continents = calculateCoordinate(6, 290, 1, 1);
    } else {
      this.bigRadius = calculateCoordinate(6, 640, 1, 1);
      this.circlesVail = calculateCoordinate(4, 150, 1, 1, 0);
      this.circles = calculateCoordinate(6, 250, 1, 1);
      this.continents = calculateCoordinate(6, 320, 1, 1);
    }
    this.letContinents();
  }

  letContinents() {
    this.continents.forEach((v, index) => {
      v.xb = this.bigRadius[index].x;
      v.yb = this.bigRadius[index].y;
      v.id = Number(index);
      v.icon = this.destinations.icons[index];
      v.url = this.destinations.urls[index];
      if (index == this.continents.length - 1) {
        v.title = this.destinations.titles[this.destinations.titles.length - 1];
        v.icon = this.destinations.icons[this.destinations.icons.length - 1];
      } else {
        v.title = this.destinations.titles[index];
      }
    });
  }

  changeCircleElements(circleIndex: number, length: number, clockwise: boolean) {
    if (clockwise) {
      const i = (this.circleItemIndex + 3) % length;
      const j = (circleIndex + 2) % 6;
      this.continents[j].title = this.destinations.titles[i];
      this.continents[j].icon = this.destinations.icons[i];
      this.continents[j].iconA = this.destinations.icons[i];
      this.continents[j].url = this.destinations.urls[i];
      this.circleItemIndex = (this.circleItemIndex + 1) % length;
    } else {
      const i = (this.circleItemIndex - 2 + length) % length;
      const j = (circleIndex - 1 + 6) % 6;
      this.continents[j].title = this.destinations.titles[i];
      this.continents[j].icon = this.destinations.icons[i];
      this.continents[j].iconA = this.destinations.icons[i];
      this.continents[j].url = this.destinations.urls[i];
      this.circleItemIndex = (this.circleItemIndex - 1 + length) % length;
    }
  }

  getOpacity(hover?: boolean) {
    if (this.mode == 'Continents') {
      return 'opacity: 100%;';
    }
    return hover ? 'opacity: 90%;' : 'opacity: 70%;';
  }

  protected readonly Icons = Icons;
}
